import React, { useState, useEffect, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Table, Form, Button, Alert, Badge, Modal } from 'react-bootstrap';
import { hasPermission } from './utils/permissions';
import { isSuperAdmin } from './utils/superAdmin';
import { useAuth } from './context/AuthContext';

const InnovationAwards = () => {
  const { user } = useAuth();

  // 狀態變數設定
  const [canEditRole, setCanEditRole] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'term', direction: 'desc' });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [tableKey, setTableKey] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState('auto');
  const observer = useRef();
  const lastAwardRef = useRef();

  // 新增/編輯模態框狀態
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' 或 'edit'
  const [currentAward, setCurrentAward] = useState(null);
  const [formData, setFormData] = useState({
    term: '',
    no: '',
    creator: '',
    recommend_unit: '',
    category: '',
    award_level: '',
    project: '',
    project_story: '',
    document_url: '',
    presentation_url: '',
    video_url: ''
  });

  // 網址編輯模態框狀態（給社友使用）
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlFormData, setUrlFormData] = useState({
    document_url: '',
    presentation_url: '',
    video_url: ''
  });
  const [canEditUrls, setCanEditUrls] = useState(false);

  // 獎項類別選項
  const categoryOptions = [
    '個人創新',
    '新創企劃',
    '服務行銷',
    '生產類',
    '研發製造',
    '社務創新',
    '製造科技'
  ];

  // 獎別選項
  const awardLevelOptions = [
    '傑出獎',
    '卓越獎',
    '優良獎',
    '佳作獎',
    '菁英獎',
    '參與獎'
  ];

  // 檢查權限函數
  const checkPermission = async () => {
    const editRolePermission = await hasPermission(user, 'EDIT_ROLE');
    setCanEditRole(editRolePermission);

    // 檢查刪除權限：e_id=80 或各社秘書(e_position=17)
    const canDeletePermission = user && (
      isSuperAdmin(user.e_id) ||
      parseInt(user.e_position) === 17
    );
    setCanDelete(canDeletePermission);

    // 檢查網址編輯權限：所有社友(e_group=5)都可以編輯網址
    const canEditUrlsPermission = user && parseInt(user.e_group) === 5;
    setCanEditUrls(canEditUrlsPermission);
  };

  // 取得獎項資料
  const fetchAwards = async (page, sortKey = 'term', sortDirection = 'desc', filter = '', isNewQuery = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/innovation-awards', {
        params: { filter, page, sortKey, sortDirection },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (isNewQuery) {
        setAwards(response.data);
      } else {
        setAwards((prevAwards) => [...prevAwards, ...response.data]);
      }
      setHasMore(response.data.length > 0);
    } catch (error) {
      console.error('取得歷屆創新獎項資料錯誤:', error);
      setErrorMessage('取得資料失敗，請稍後再試');
      setTimeout(() => setErrorMessage(''), 3000);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAwards(page, sortConfig.key, sortConfig.direction, filter, page === 1);
  }, [page, filter, sortConfig]);

  // 排序處理
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(1);
    fetchAwards(1, key, direction, filter, true);
  };

  // 篩選處理
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
    setPage(1);
    fetchAwards(1, sortConfig.key, sortConfig.direction, e.target.value, true);
  };

  // 無限滾動
  useEffect(() => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setPage((prevPage) => prevPage + 1);
      }
    });

    if (lastAwardRef.current) {
      observer.current.observe(lastAwardRef.current);
    }
  }, [loading, hasMore]);

  // 開啟新增模態框
  const handleAdd = () => {
    setModalMode('add');
    setCurrentAward(null);
    setFormData({
      term: '',
      no: '',
      creator: '',
      recommend_unit: '',
      category: '',
      award_level: '',
      project: '',
      project_story: '',
      document_url: '',
      presentation_url: '',
      video_url: ''
    });
    setShowModal(true);
  };

  // 開啟編輯模態框（完整編輯，僅管理員）
  const handleEdit = (award) => {
    setModalMode('edit');
    setCurrentAward(award);
    setFormData({
      term: award.term,
      no: award.no,
      creator: award.creator,
      recommend_unit: award.recommend_unit,
      category: award.category,
      award_level: award.award_level,
      project: award.project,
      project_story: award.project_story || '',
      document_url: award.document_url || '',
      presentation_url: award.presentation_url || '',
      video_url: award.video_url || ''
    });
    setShowModal(true);
  };

  // 開啟網址編輯模態框（僅網址，給社友使用）
  const handleEditUrls = (award) => {
    setCurrentAward(award);
    setUrlFormData({
      document_url: award.document_url || '',
      presentation_url: award.presentation_url || '',
      video_url: award.video_url || ''
    });
    setShowUrlModal(true);
  };

  // 表單輸入處理
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 網址表單輸入處理
  const handleUrlInputChange = (e) => {
    const { name, value } = e.target;
    setUrlFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 提交表單（新增或編輯）
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 驗證必填欄位
    if (!formData.term || !formData.no || !formData.creator || !formData.recommend_unit ||
        !formData.category || !formData.award_level || !formData.project) {
      setErrorMessage('請填寫所有必填欄位');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }

    try {
      const token = localStorage.getItem('token');

      if (modalMode === 'add') {
        // 新增
        await axios.post('/innovation-awards', formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSuccessMessage('新增成功！');
      } else {
        // 編輯
        await axios.put(`/innovation-awards/${currentAward.id}`, formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSuccessMessage('更新成功！');
      }

      setTimeout(() => setSuccessMessage(''), 3000);
      setShowModal(false);

      // 重新載入資料
      setPage(1);
      fetchAwards(1, sortConfig.key, sortConfig.direction, filter, true);
      setTableKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.error('提交失敗:', error);
      let errorMsg = modalMode === 'add' ? '新增失敗' : '更新失敗';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  // 提交網址表單（僅社友）
  const handleUrlSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/innovation-awards/${currentAward.id}/urls`, urlFormData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccessMessage('網址更新成功！');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowUrlModal(false);

      // 重新載入資料
      setPage(1);
      fetchAwards(1, sortConfig.key, sortConfig.direction, filter, true);
      setTableKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.error('網址更新失敗:', error);
      let errorMsg = '網址更新失敗';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      setErrorMessage(errorMsg);
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  // 刪除處理
  const handleDelete = async (id) => {
    if (window.confirm('確定要刪除這筆創新獎項資料嗎？')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/innovation-awards/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setAwards((prevAwards) => prevAwards.filter((award) => award.id !== id));
        setSuccessMessage('刪除成功！');
        setTimeout(() => setSuccessMessage(''), 3000);
        setTableKey((prevKey) => prevKey + 1);
      } catch (error) {
        let errorMsg = '刪除失敗';
        if (error.response?.data?.message) {
          errorMsg = error.response.data.message;
        } else if (error.message) {
          errorMsg = `刪除失敗: ${error.message}`;
        }
        setErrorMessage(errorMsg);
        setTimeout(() => setErrorMessage(''), 5000);
      }
    }
  };

  useEffect(() => {
    checkPermission();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 獎項類別顏色映射
  const getCategoryBadge = (category) => {
    const categoryColors = {
      '個人創新': 'primary',
      '新創企劃': 'success',
      '服務行銷': 'info',
      '生產類': 'warning',
      '研發製造': 'danger',
      '社務創新': 'secondary',
      '製造科技': 'dark'
    };
    return categoryColors[category] || 'light';
  };

  // 獎別顏色映射
  const getAwardLevelBadge = (awardLevel) => {
    if (awardLevel.includes('傑出')) return 'danger';
    if (awardLevel.includes('卓越')) return 'danger';
    if (awardLevel.includes('優良')) return 'warning';
    if (awardLevel.includes('佳作')) return 'success';
    if (awardLevel.includes('菁英')) return 'primary';
    if (awardLevel.includes('參與')) return 'info';
    return 'secondary';
  };

  return (
    <div className="container-fluid mt-4" key={tableKey}>
      {/* 顯示成功訊息 */}
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </Alert>
      )}

      {/* 顯示錯誤訊息 */}
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
          {errorMessage}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <Form.Control
          type="text"
          placeholder="搜尋創作者、推薦單位、獎項類別、獎別、創新項目..."
          value={filter}
          style={{ flex: '1', minWidth: '200px' }}
          onChange={handleFilterChange}
        />
        <div className="d-flex align-items-center gap-2">
          {/* 新增按鈕 */}
          {canDelete && (
            <Button variant="success" onClick={handleAdd}>
              <i className="fas fa-plus me-1"></i>
              新增獎項
            </Button>
          )}

          {/* 視圖切換按鈕 */}
          <div className="btn-group btn-group-sm" role="group">
            <Button
              variant={viewMode === 'auto' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('auto')}
              title="自動切換"
            >
              📱
            </Button>
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('table')}
              title="表格檢視"
            >
              📊
            </Button>
            <Button
              variant={viewMode === 'card' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setViewMode('card')}
              title="卡片檢視"
            >
              🃏
            </Button>
          </div>
        </div>
      </div>

      {/* 新增/編輯模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? '新增創新獎項' : '編輯創新獎項'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>屆別 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    name="term"
                    value={formData.term}
                    onChange={handleInputChange}
                    required
                    min="1"
                    max="99"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>NO <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    name="no"
                    value={formData.no}
                    onChange={handleInputChange}
                    required
                    min="1"
                    max="999"
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>創作者 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="creator"
                    value={formData.creator}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>推薦單位 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="recommend_unit"
                    value={formData.recommend_unit}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>獎項類別 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">請選擇...</option>
                    {categoryOptions.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>獎別/等級 <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="award_level"
                    value={formData.award_level}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">請選擇...</option>
                    {awardLevelOptions.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>創新項目 <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="project"
                value={formData.project}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>創新事蹟（選填）</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="project_story"
                value={formData.project_story}
                onChange={handleInputChange}
              />
            </Form.Group>

            <hr />
            <h6 className="mb-3">相關連結（選填）</h6>

            <Form.Group className="mb-3">
              <Form.Label>文件網址</Form.Label>
              <Form.Control
                type="url"
                name="document_url"
                value={formData.document_url}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>簡報網址</Form.Label>
              <Form.Control
                type="url"
                name="presentation_url"
                value={formData.presentation_url}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>影片網址</Form.Label>
              <Form.Control
                type="url"
                name="video_url"
                value={formData.video_url}
                onChange={handleInputChange}
                placeholder="https://..."
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                取消
              </Button>
              <Button variant="primary" type="submit">
                {modalMode === 'add' ? '新增' : '更新'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* 網址編輯模態框（給社友使用） */}
      <Modal show={showUrlModal} onHide={() => setShowUrlModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>編輯相關連結</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUrlSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>文件網址</Form.Label>
              <Form.Control
                type="url"
                name="document_url"
                value={urlFormData.document_url}
                onChange={handleUrlInputChange}
                placeholder="https://..."
              />
              <Form.Text className="text-muted">
                請輸入完整的網址，例如：https://example.com/document.pdf
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>簡報網址</Form.Label>
              <Form.Control
                type="url"
                name="presentation_url"
                value={urlFormData.presentation_url}
                onChange={handleUrlInputChange}
                placeholder="https://..."
              />
              <Form.Text className="text-muted">
                請輸入完整的網址，例如：https://example.com/slides.ppt
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>影片網址</Form.Label>
              <Form.Control
                type="url"
                name="video_url"
                value={urlFormData.video_url}
                onChange={handleUrlInputChange}
                placeholder="https://..."
              />
              <Form.Text className="text-muted">
                請輸入完整的網址，例如：https://youtube.com/watch?v=...
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowUrlModal(false)}>
                取消
              </Button>
              <Button variant="primary" type="submit">
                儲存
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* 資料顯示區域 - 根據模式切換 */}
      {(viewMode === 'card' || (viewMode === 'auto' && isMobile)) ? (
        /* 卡片式佈局 - 適合手機端 */
        <div className="row">
          {awards.map((award, index) => (
            <div key={award.id} className="col-12 col-md-6 col-lg-4 mb-3" ref={awards.length === index + 1 ? lastAwardRef : null}>
              <div className="card h-100 shadow-sm">
                <div className="card-header d-flex justify-content-between align-items-center bg-dark text-white">
                  <h6 className="mb-0">第 {award.term} 屆</h6>
                  <span className="badge bg-primary">NO.{award.no}</span>
                </div>
                <div className="card-body">
                  <div className="row g-2 small">
                    <div className="col-12">
                      <strong>創新項目：</strong><br />
                      <span className="text-dark fw-bold">{award.project}</span>
                    </div>
                    <div className="col-6">
                      <strong>創作者：</strong><br />
                      <span className="text-muted">{award.creator}</span>
                    </div>
                    <div className="col-6">
                      <strong>推薦單位：</strong><br />
                      <span className="text-muted">{award.recommend_unit}</span>
                    </div>
                    <div className="col-12">
                      <strong>獎項類別：</strong><br />
                      <Badge bg={getCategoryBadge(award.category)} className="me-1">
                        {award.category}
                      </Badge>
                      <Badge bg={getAwardLevelBadge(award.award_level)}>
                        {award.award_level}
                      </Badge>
                    </div>
                    {award.project_story && (
                      <div className="col-12">
                        <strong>創新事蹟：</strong><br />
                        <span className="text-muted small">{award.project_story}</span>
                      </div>
                    )}
                    {(award.document_url || award.presentation_url || award.video_url) && (
                      <div className="col-12">
                        <strong>相關連結：</strong><br />
                        <div className="d-flex flex-wrap gap-1">
                          {award.document_url && (
                            <a href={award.document_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                              <i className="fas fa-file-alt me-1"></i>文件
                            </a>
                          )}
                          {award.presentation_url && (
                            <a href={award.presentation_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success">
                              <i className="fas fa-file-powerpoint me-1"></i>簡報
                            </a>
                          )}
                          {award.video_url && (
                            <a href={award.video_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger">
                              <i className="fas fa-video me-1"></i>影片
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {(canDelete || canEditUrls) && (
                  <div className="card-footer bg-transparent">
                    <div className="d-flex gap-2">
                      {canDelete ? (
                        <>
                          <Button
                            variant="info"
                            size="sm"
                            className="flex-fill"
                            onClick={() => handleEdit(award)}
                          >
                            編輯
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            className="flex-fill"
                            onClick={() => handleDelete(award.id)}
                          >
                            刪除
                          </Button>
                        </>
                      ) : canEditUrls ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="flex-fill"
                          onClick={() => handleEditUrls(award)}
                        >
                          <i className="fas fa-link me-1"></i>編輯網址
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div className="col-12 text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">載入中...</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* 表格式佈局 - 適合桌面端 */
        <div style={{ overflowX: 'auto' }}>
          <Table striped bordered hover variant="dark" className="table-striped table-dark" style={{
            tableLayout: 'auto',
            minWidth: '1000px'
          }}>
            <thead>
              <tr>
                <th onClick={() => handleSort('term')} style={{
                  width: '60px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>屆別</th>
                <th onClick={() => handleSort('no')} style={{
                  width: '50px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>NO</th>
                <th onClick={() => handleSort('creator')} style={{
                  width: '100px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>創作者</th>
                <th onClick={() => handleSort('recommend_unit')} style={{
                  width: '100px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>推薦單位</th>
                <th onClick={() => handleSort('category')} style={{
                  width: '100px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>獎項類別</th>
                <th onClick={() => handleSort('award_level')} style={{
                  width: '100px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>獎別/等級</th>
                <th onClick={() => handleSort('project')} style={{
                  minWidth: '200px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>創新項目</th>
                {(canDelete || canEditUrls) && (
                  <th style={{
                    width: '120px',
                    fontSize: '14px'
                  }}>操作</th>
                )}
              </tr>
            </thead>
            <tbody>
              {awards.map((award, index) => (
                <tr key={award.id} ref={awards.length === index + 1 ? lastAwardRef : null}>
                  <td style={{ textAlign: 'center', fontSize: '14px' }}>
                    <Badge bg="primary">第 {award.term} 屆</Badge>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '14px' }}>{award.no}</td>
                  <td style={{
                    wordBreak: 'break-word',
                    fontSize: '14px',
                    padding: '8px'
                  }}>
                    {award.creator}
                  </td>
                  <td style={{
                    wordBreak: 'break-word',
                    fontSize: '13px',
                    padding: '8px'
                  }}>
                    {award.recommend_unit}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '13px' }}>
                    <Badge bg={getCategoryBadge(award.category)}>
                      {award.category}
                    </Badge>
                  </td>
                  <td style={{ textAlign: 'center', fontSize: '13px' }}>
                    <Badge bg={getAwardLevelBadge(award.award_level)}>
                      {award.award_level}
                    </Badge>
                  </td>
                  <td style={{
                    wordBreak: 'break-word',
                    fontSize: '14px',
                    padding: '8px'
                  }}>
                    <div>{award.project}</div>
                    {(award.document_url || award.presentation_url || award.video_url) && (
                      <div className="mt-2 d-flex flex-wrap gap-1">
                        {award.document_url && (
                          <a href={award.document_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary" style={{ fontSize: '11px' }}>
                            <i className="fas fa-file-alt me-1"></i>文件
                          </a>
                        )}
                        {award.presentation_url && (
                          <a href={award.presentation_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-success" style={{ fontSize: '11px' }}>
                            <i className="fas fa-file-powerpoint me-1"></i>簡報
                          </a>
                        )}
                        {award.video_url && (
                          <a href={award.video_url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger" style={{ fontSize: '11px' }}>
                            <i className="fas fa-video me-1"></i>影片
                          </a>
                        )}
                      </div>
                    )}
                  </td>
                  {(canDelete || canEditUrls) && (
                    <td style={{
                      textAlign: 'center',
                      padding: '8px',
                      whiteSpace: 'nowrap'
                    }}>
                      {canDelete ? (
                        <>
                          <Button
                            variant="info"
                            size="sm"
                            style={{ fontSize: '12px', marginRight: '4px' }}
                            onClick={() => handleEdit(award)}
                          >
                            編輯
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            style={{ fontSize: '12px' }}
                            onClick={() => handleDelete(award.id)}
                          >
                            刪除
                          </Button>
                        </>
                      ) : canEditUrls ? (
                        <Button
                          variant="primary"
                          size="sm"
                          style={{ fontSize: '12px' }}
                          onClick={() => handleEditUrls(award)}
                        >
                          <i className="fas fa-link me-1"></i>編輯網址
                        </Button>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
              {loading && (
                <tr>
                  <td colSpan={(canDelete || canEditUrls) ? 8 : 7} className="text-center">
                    載入中...
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default InnovationAwards;
